// Core Components
import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { Redirect } from 'react-router-dom';

// Material UI Theme Imports
import InputAdornment from '@material-ui/core/InputAdornment';
import IconButton from '@material-ui/core/IconButton';
import Visibility from '@material-ui/icons/Visibility';
import VisibilityOff from '@material-ui/icons/VisibilityOff';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import DialogTitle from '@material-ui/core/DialogTitle';
import { TextField, Button, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useSelector } from 'react-redux';

// Application Specific Imports
import { userLogin } from 'redux/actions';
import logo from 'assets/logo.svg';
import config from '../config';
import { get, noop, size } from 'global/lib/lodash';
import { Loader } from 'global/styles';
import EImage from 'global/components/image';
import { Row } from 'global/components/row';
import { HelpModal } from './HelpModal';

const useStyles = makeStyles((theme) => ({
  appLogo: {
    width: '250px',
    height: '150px',
    margin: `${theme.spacing(1)}px auto`,
    pointerEvents: 'none',
  },
  appLogin: {
    width: 'auto',
    display: 'block',
    marginLeft: theme.spacing.unit * 3,
    marginRight: theme.spacing.unit * 3,
    textAlign: 'center',
  },

  loginForm: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'space-around',
    marginTop: theme.spacing(1),
  },

  loginFormWrapper: {
    width: '100%',
    marginTop: theme.spacing(1),
  },

  button: {
    margin: theme.spacing(1),
  },

  policy: {
    display: 'block',
    marginTop: theme.spacing(2),
  },
}));

function LoginPage(props) {
  const [username, changeUsername] = useState('');
  const [password, changePassword] = useState('');
  const [loginSubmitted, changeLoginSubmitted] = useState('');
  const [showPassword, changeShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const loadedTheme = useSelector((state) => get(state, 'view.hasLoadedTheme'));
  const loadedAccount = useSelector((state) => get(state, 'auth.accountSettings'));
  const [modal, setModal] = useState({
    open: false,
    onClose: () => setModal((s) => ({ ...s, open: !s.open })),
  });

  const classes = useStyles();
  const { loggedIn, loginFailed } = props;
  const showFailedDialog = !loggedIn && loginFailed === true && loginSubmitted;

  useEffect(() => {
    if (showFailedDialog) {
      setLoading(false);
    }
  }, [showFailedDialog]);

  const renderRedirect = (redirect) => {
    if (redirect && loadedTheme && !!size(loadedAccount)) {
      return <Redirect to="/home" />;
    }
  };

  const usernameInputChange = () => (event) => {
    changeUsername(event.target.value);
  };

  const passwordInputChange = () => (event) => {
    changePassword(event.target.value);
  };

  const textInputReset = () => (e) => {
    changeUsername('');
    changePassword('');
    changeShowPassword(false);
    e.preventDefault();
  };

  const handleLoginClick = () => (e) => {
    setLoading(true);
    const usernameLogin = username !== undefined ? username : false;
    const passwordLogin = password !== undefined ? password : false;
    changeLoginSubmitted(true);
    props.dispatch(userLogin(usernameLogin, passwordLogin));
    e.preventDefault();
  };

  const handleDialogClose = () => {
    changeLoginSubmitted(false);
  };

  const handleClickShowPassword = () => {
    changeShowPassword(!showPassword);
  };

  return (
    <div className={classes.appLogin}>
      <Dialog open={showFailedDialog} onClose={handleDialogClose} aria-labelledby="alert-dialog-title" aria-describedby="alert-dialog-description">
        <DialogTitle id="alert-dialog-title">{'Login Attempt Failed'}</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">Your login credentials were not accepted.</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose} color="primary" autoFocus>
            Oops
          </Button>
        </DialogActions>
      </Dialog>
      {renderRedirect(loggedIn)}
      <section className={classes.header}>
        <img src={logo} className={classes.appLogo} alt="logo" />
      </section>
      <form className={classes.loginForm} noValidate autoComplete="off">
        <div className={classes.loginFormWrapper}>
          <TextField required id="username" label="Email" className="login-username" value={username} onChange={usernameInputChange()} variant="outlined" style={{ width: '266px' }} />
        </div>
        <div className={classes.loginFormWrapper}>
          <TextField
            required
            id="password"
            label="Password"
            className="login-password"
            variant="outlined"
            type={showPassword ? 'text' : 'password'}
            value={password}
            style={{ marginTop: 'inherit' }}
            onChange={passwordInputChange()}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton aria-label="Toggle password visibility" onClick={handleClickShowPassword}>
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </div>
        <div className={classes.loginFormWrapper}>
          <Button
            type="submit"
            className={classes.button}
            style={{ width: '266px' }}
            variant="contained"
            color="primary"
            label="Login"
            disabled={!username || !password}
            onClick={loading ? noop : handleLoginClick()}
            endIcon={loading && <Loader size={14} color={'inherit'} />}
          >
            Login
          </Button>
        </div>
        <div className={classes.loginFormWrapper}>
          <Button label="Clear" className={classes.button} variant="contained" onClick={textInputReset()} disabled={!username && !password}>
            Clear
          </Button>
          <Button
            label="Forgot Password"
            className={classes.button}
            variant="contained"
            onClick={() => {
              window.location.replace(`${config.api.baseUrl}password/reset`);
            }}
          >
            Forgot Password
          </Button>
        </div>
      </form>
      <Typography variant="caption" className={classes.policy}>
        By logging in you agree to the <a href="/policy">Privacy Policy</a> and <a href="/policy/terms">User Agreement</a>
      </Typography>

      <Row center style={{ marginTop: 16 }}>
        <Button variant="contained" color="primary" onClick={() => setModal((s) => ({ ...s, open: !s.open }))}>
          Need Help ?
        </Button>
      </Row>
      <HelpModal {...modal} />
    </div>
  );
}

const mapStateToProps = (state) => {
  return {
    loggedIn: state.auth.accessToken,
    loginFailed: state.auth.loginFailed,
  };
};

export default connect(mapStateToProps)(LoginPage);
